import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Divider, useTheme } from '@material-ui/core';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

import { SText } from '@setvi/shared/components';
import { Product } from '@setvi/shared/interfaces';
import { CollapseIcon, MessageIcon } from '@setvi/shared/components/sicons';
import { ProductMatchSubmitButton } from '@setvi/shared/components/intelligence/product-match/components/form/components';

import { AILoader } from 'pages/products/components';
import { aiProductChatMessages } from 'pages/products/components/ai/loader/messages';

import {
  CollapseButton,
  Container,
  Content,
  Footer,
  Header,
  MsgContainer,
  QuestionButton,
  QuestionContainer,
  QuestionInput,
  QuestionMsg
} from './styled';
import { ChatFeedback } from './components';
import { ConversationProps } from './hook/use-ask-ai';
import { useAskAIStorage } from './store';

interface ProductAskAIProps {
  onClose: () => void;
  conversation: ConversationProps[];
  onSubmit: (question: string) => void;
  loading?: boolean;
  initLoading?: boolean;
  product: Product;
  questions?: string[];
  hideHeader?: boolean;
  setConversation: React.Dispatch<React.SetStateAction<ConversationProps[]>>;
}

const ProductAskAI = ({
  onClose,
  setConversation,
  onSubmit,
  hideHeader,
  conversation,
  loading,
  product,
  questions,
  initLoading
}: ProductAskAIProps) => {
  const { spacing } = useTheme();

  const { value, setValue } = useAskAIStorage();

  const contentRef = useRef<HTMLDivElement>(null);
  const [currentMessage, setCurrentMessage] = useState(value || '');

  const handleAskAI = (question: string) => {
    onSubmit(question);
    setCurrentMessage('');
    setValue('');
  };

  useLayoutEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [conversation]);

  return (
    <Container>
      {!hideHeader && (
        <Box>
          <Header>
            <SText title="Ask AI" weight="bold" noWrap />
            <CollapseButton
              color="default"
              variant="outlined"
              onClick={onClose}
              startIcon={<CollapseIcon fill="#5D7083" />}>
              Close
            </CollapseButton>
          </Header>
          <Divider />
        </Box>
      )}

      {initLoading ? (
        <Box
          padding={spacing(2)}
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <AILoader messages={aiProductChatMessages} />
        </Box>
      ) : (
        <>
          <Content
            // @ts-ignore
            ref={contentRef}>
            {!conversation?.length ? (
              <Box>
                <SText
                  title="Suggested Questions"
                  fontColor="#20262D"
                  size="sm"
                  noWrap
                />
                <QuestionContainer>
                  {questions?.map(q => (
                    <QuestionButton key={q} onClick={() => handleAskAI(q)}>
                      {q}
                    </QuestionButton>
                  ))}
                </QuestionContainer>
              </Box>
            ) : (
              conversation?.map(
                ({ role, content, id, loading: aiLoading }, index) => (
                  <>
                    {role === 'user' && (
                      <MsgContainer justifyContent="flex-end">
                        <QuestionMsg>{content}</QuestionMsg>
                      </MsgContainer>
                    )}
                    {role === 'assistant' && (
                      <MsgContainer
                        justifyContent="flex-start"
                        flexDirection="column"
                        gridGap={12}>
                        <MessageIcon />

                        {loading && index === conversation?.length - 1 ? (
                          <Box display="flex" gridGap={8}>
                            <Box width={20} height={20}>
                              <DotLottieReact
                                loop
                                autoplay
                                src="/images/lottie/loading-star.lottie"
                                style={{ width: '100%', height: '100%' }}
                              />
                            </Box>
                            <SText title="Checking..." noWrap />
                          </Box>
                        ) : (
                          <>
                            <div
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{ __html: content }}
                            />

                            {!aiLoading &&
                              index === conversation?.length - 1 && (
                                <ChatFeedback
                                  id={id}
                                  product={product}
                                  conversation={conversation}
                                  setConversation={setConversation}
                                />
                              )}
                          </>
                        )}
                      </MsgContainer>
                    )}
                  </>
                )
              )
            )}
          </Content>

          <Footer>
            <QuestionInput
              placeholder="Ask a question"
              value={currentMessage}
              variant="outlined"
              onKeyDown={e => {
                if (
                  e.key === 'Enter' &&
                  !e.shiftKey &&
                  !loading &&
                  !!currentMessage
                ) {
                  e.preventDefault();
                  handleAskAI(currentMessage);
                }
              }}
              onChange={e => setCurrentMessage(e.target.value)}
              multiline
              minRows={1}
              maxRows={6}
            />
            <ProductMatchSubmitButton
              expanded={false}
              tooltipTitle={!currentMessage ? 'Add a question' : ''}
              disabled={!currentMessage || !!loading}
              onClick={() => handleAskAI(currentMessage)}
            />
          </Footer>
        </>
      )}
    </Container>
  );
};

export default ProductAskAI;
